/**
 * THE TYPED API CLIENT THE SCREENS USE (D259, ADR-011 §5).
 *
 * One object a screen talks to; nothing else in the screen layer knows a
 * URL, a header or a status code. Every command body and every view query
 * is typed from the same schemas the door validates against, so a screen
 * that compiles sends what the server accepts, and every refusal arrives as
 * an `ApiError` carrying the door's code, message and field issues — the
 * material for an actionable message next to the field that caused it.
 *
 * Transport is injected (`fetch` in a browser, a stub in a test), so this
 * file compiles in the platform library (no DOM here — C1) and runs
 * unchanged in the browser the React screens will live in.
 */
import type { Infer } from '../platform/schema.js';
import type * as S from '../platform/command-schemas.js';
import type * as outbox from '../views/outbox.js';
import type * as filling from '../views/filling.js';
import type * as cutoverViews from '../views/cutover.js';
import type * as bills from '../views/bills.js';
import type * as reports from '../views/reports.js';
import type * as customers from '../views/customers.js';
import type * as documents from '../views/documents.js';
import type * as cylinders from '../views/cylinders.js';
import type * as passportView from '../views/passport.js';
import type * as damageViews from '../views/damage.js';
import type * as labelViews from '../views/labels.js';
import type * as maintenance from '../views/maintenance.js';
import type * as dispatch from '../views/dispatch.js';
import type * as account from '../views/account.js';
import type * as payments from '../views/payments.js';
import type * as allocationViews from '../views/allocation.js';
import type * as statementView from '../views/statement.js';
import type * as audit from '../views/audit.js';
import type * as home from '../views/home.js';
import type * as figures from '../views/figures.js';
import type * as health from '../views/health.js';
import type * as approvalsBook from '../views/approvals.js';
import type * as orders from '../views/orders.js';
import type * as masters from '../views/masters.js';
import type * as setup from '../views/setup.js';
import type * as people from '../views/people.js';
import type * as tenders from '../views/tenders.js';
import type * as quotes from '../views/quotations.js';
import type * as fulfilment from '../views/fulfilment.js';
import type * as purchasing from '../views/purchasing.js';
import type * as production from '../views/production.js';
import type * as contestedIntakes from '../views/contestedIntakes.js';
import type * as yard from '../views/yard.js';
import { IDEMPOTENCY_HEADER } from '../platform/wire.js';

/** The body of each command, as the door accepts it (no idempotency key — that is a header). */
export interface CommandBodies {
  'challan.createDispatch': Infer<typeof S.createDispatch>;
  'challan.createReturn': Infer<typeof S.createReturn>;
  'challan.approve': Infer<typeof S.approveChallan>;
  'challan.reject': Infer<typeof S.rejectChallan>;
  'challan.reverse': Infer<typeof S.reverseChallan>;
  'damage.assess': Infer<typeof S.assessDamage>;
  'damage.dispute': Infer<typeof S.disputeDamage>;
  'damage.uphold': Infer<typeof S.upholdDamage>;
  'damage.reduce': Infer<typeof S.reduceDamage>;
  'damage.waive': Infer<typeof S.waiveDamage>;
  'damage.void': Infer<typeof S.voidDamage>;
  'dispatch.depart': Infer<typeof S.recordDeparture>;
  'dispatch.confirmDelivery': Infer<typeof S.confirmDelivery>;
  'dispatch.resolveShortage': Infer<typeof S.resolveShortage>;
  'cylinder.register': Infer<typeof S.registerCylinder>;
  // Slice 3 — the label a cylinder wears.
  'label.configureStock': Infer<typeof S.configureLabelStock>;
  'label.prepare': Infer<typeof S.prepareLabels>;
  'label.reprint': Infer<typeof S.reprintLabels>;
  'label.generate': Infer<typeof S.generateLabelSheet>;
  'label.confirmPrinted': Infer<typeof S.confirmPrinted>;
  'label.abandon': Infer<typeof S.abandonBatch>;
  'label.scan': Infer<typeof S.recordScan>;
  'cylinder.correct': Infer<typeof S.correctCylinderDetails>;
  'cylinder.transferOwner': Infer<typeof S.transferOwnership>;
  'cylinder.endLife': Infer<typeof S.endCylinderLife>;
  'cylinder.bulkRegister': Infer<typeof S.bulkRegisterCylinders>;
  'cylinder.convertGas': Infer<typeof S.convertGasService>;
  'challan.editPendingLines': Infer<typeof S.editPendingDispatchLines>;
  'challan.addPendingCharge': Infer<typeof S.addChargeToPendingDispatch>;
  'challan.editPendingReturn': Infer<typeof S.editPendingReturnLine>;
  'cutover.load': Infer<typeof S.loadCutover>;
  'cutover.acceptOpeningBalance': Infer<typeof S.acceptOpeningBalance>;
  'cutover.settle': Infer<typeof S.settleWorklistItem>;
  'cutover.importChallans': Infer<typeof S.importCsv>;
  'cutover.importReceipts': Infer<typeof S.importCsv>;
  'cutover.importTollFill': Infer<typeof S.importCsv>;
  'bill.send': Infer<typeof S.sendBill>;
  'bill.prepareMonthly': Infer<typeof S.prepareMonthlyBill>;
  'payment.reverse': Infer<typeof S.reversePayment>;
  'user.create': Infer<typeof S.createUserWire>;   // 0172
  'sd.refund': Infer<typeof S.refundDeposit>;   // 0171
  'creditnote.issue': Infer<typeof S.issueCreditNote>;
  'payment.record': Infer<typeof S.recordPayment>;
  'payment.allocate': Infer<typeof S.allocatePayment>;
  'payment.reverseAllocation': Infer<typeof S.reverseAllocation>;
  'eventbill.resolveLate': Infer<typeof S.resolveLateReturn>;
  'notice.setState': Infer<typeof S.setNoticeState>;
  'costbill.issue': Infer<typeof S.issueCostBill>;
  'user.resetPassword': Infer<typeof S.resetUserPasswordWire>;
  'customer.create': Infer<typeof S.createCustomer>;
  'customer.update': Infer<typeof S.updateCustomer>;
  'customer.setTerms': Infer<typeof S.setCustomerTerms>;
  'site.create': Infer<typeof S.createSite>;
  'site.update': Infer<typeof S.updateSite>;
  'order.create': Infer<typeof S.createOrder>;
  'order.approve': Infer<typeof S.approveOrder>;
  'order.cancel': Infer<typeof S.cancelOrder>;
  'outbox.retry': Infer<typeof S.retryMessage>;
  'outbox.sendByHand': Infer<typeof S.sendByHand>;
  'finding.acknowledge': Infer<typeof S.acknowledgeFinding>;
  // Milestone 2 — commercial master data
  'branch.open': Infer<typeof S.openBranch>;
  'branch.update': Infer<typeof S.updateBranch>;
  'branch.close': Infer<typeof S.closeBranch>;
  'platform.assignUserBranch': Infer<typeof S.assignUserBranch>;
  // 0152 — the areas a yard is divided into, and moving a drum between them
  'yard.openArea': Infer<typeof S.openYardArea>;
  'yard.closeArea': Infer<typeof S.closeYardArea>;
  'yard.nominateQuarantineArea': Infer<typeof S.nominateQuarantineArea>;
  'cylinder.moveToArea': Infer<typeof S.moveCylinderToArea>;
  'cylinder.inspect': Infer<typeof S.inspectCylinder>;
  'cylinder.recordMaintenance': Infer<typeof S.recordMaintenance>;
  'cylinder.correctMaintenance': Infer<typeof S.correctMaintenance>;
  'cylinder.condemn': Infer<typeof S.condemnCylinder>;
  'cylinder.finalizeDestruction': Infer<typeof S.finalizeDestruction>;
  'cylinder.storeMaintenancePaper': Infer<typeof S.storeMaintenancePaper>;
  'catalogue.addGas': Infer<typeof S.addGas>;
  'catalogue.addGrade': Infer<typeof S.addGrade>;
  'catalogue.saveCharges': Infer<typeof S.saveChargeSchedule>;
  'catalogue.saveOfferText': Infer<typeof S.saveOfferText>;
  'company.saveLetterhead': Infer<typeof S.saveLetterhead>;
  'cylinder.registerDuplicate': Infer<typeof S.registerDuplicateStamping>;
  'catalogue.addSize': Infer<typeof S.addCylinderSize>;
  'catalogue.addProduct': Infer<typeof S.addProduct>;
  'catalogue.addSku': Infer<typeof S.addSku>;
  'catalogue.classifySku': Infer<typeof S.classifySku>;
  'catalogue.deactivate': Infer<typeof S.deactivateCatalogueEntry>;
  'tax.addCode': Infer<typeof S.addTaxCode>;
  'tax.recordRate': Infer<typeof S.recordTaxRate>;
  'price.draftList': Infer<typeof S.draftPriceList>;
  'price.set': Infer<typeof S.setPrice>;
  'price.activateList': Infer<typeof S.activatePriceList>;
  'price.assign': Infer<typeof S.assignPriceList>;
  'price.closeAssignment': Infer<typeof S.closePriceAssignment>;
  'customer.recordRegistration': Infer<typeof S.recordRegistration>;
  'customer.closeRegistration': Infer<typeof S.closeRegistration>;
  'customer.setAddress': Infer<typeof S.setAddress>;
  'customer.addContact': Infer<typeof S.addContact>;
  'customer.updateContact': Infer<typeof S.updateContact>;
  'site.setTerms': Infer<typeof S.setSiteTerms>;
  'site.endTerms': Infer<typeof S.endSiteTerms>;
  'role.create': Infer<typeof S.createRole>;
  'role.grant': Infer<typeof S.grantToRole>;
  'role.revoke': Infer<typeof S.revokeFromRole>;
  'role.assign': Infer<typeof S.assignRole>;
  'role.unassign': Infer<typeof S.unassignRole>;
  'calendar.openFinancialYear': Infer<typeof S.openFinancialYear>;
  'calendar.closePeriod': Infer<typeof S.closePeriod>;
  'calendar.reopenPeriod': Infer<typeof S.reopenPeriod>;
  'pricing.map': Infer<typeof S.mapLegacyCode>;
  'pricing.switch': Infer<typeof S.switchPricing>;
  'quote.draft': Infer<typeof S.draftQuotation>;
  'quote.setLines': Infer<typeof S.setQuotationLines>;
  'quote.issue': Infer<typeof S.issueQuotation>;
  'quote.revise': Infer<typeof S.reviseQuotation>;
  'quote.decide': Infer<typeof S.decideQuotation>;
  'quote.submit': Infer<typeof S.submitQuotation>;
  'quote.approve': Infer<typeof S.approveQuotation>;
  'order.amend': Infer<typeof S.amendOrder>;
  'order.allocate': Infer<typeof S.allocateCylinder>;
  'order.release': Infer<typeof S.releaseAllocation>;
  'supplier.create': Infer<typeof S.createSupplier>;
  'purchase.setCharges': Infer<typeof S.setPurchaseCharges>;
  'supplier.update': Infer<typeof S.updateSupplier>;
  'supplier.close': Infer<typeof S.closeSupplier>;
  'po.raise': Infer<typeof S.raisePurchaseOrder>;
  'po.setLines': Infer<typeof S.setPurchaseLines>;
  'po.approve': Infer<typeof S.approvePurchaseOrder>;
  'po.send': Infer<typeof S.sendPurchaseOrder>;
  'po.cancel': Infer<typeof S.cancelPurchaseOrder>;
  'po.amend': Infer<typeof S.amendPurchaseOrder>;
  'receipt.record': Infer<typeof S.recordReceipt>;
  'tender.record': Infer<typeof S.recordTender>;
  'tender.correct': Infer<typeof S.correctTender>;
  'tender.move': Infer<typeof S.moveTender>;
  'tender.submitBid': Infer<typeof S.submitBid>;
  'tender.addRequirement': Infer<typeof S.addTenderRequirement>;
  'tender.assessRequirement': Infer<typeof S.assessTenderRequirement>;
  'tender.supersedeRequirement': Infer<typeof S.supersedeTenderRequirement>;
  'tender.addDocument': Infer<typeof S.addTenderDocument>;
  'tender.supersedeDocument': Infer<typeof S.supersedeTenderDocument>;
  'tender.voidDocument': Infer<typeof S.voidTenderDocument>;
  'tender.setLines': Infer<typeof S.setTenderLines>;
  'tender.setDeliveryConditions': Infer<typeof S.setTenderDeliveryConditions>;
  'tender.addContact': Infer<typeof S.addTenderContact>;
  'tender.updateContact': Infer<typeof S.updateTenderContact>;
  'job.create': Infer<typeof S.createCustomerJobDraft>;
  'job.edit': Infer<typeof S.editCustomerJobDraft>;
  'job.receive': Infer<typeof S.approveCustomerJob>;
  'job.inspect': Infer<typeof S.recordCylinderInspection>;
  'job.releaseQuarantine': Infer<typeof S.releaseQuarantinedCylinder>;
  // Slice 4A — the same safety work for a vessel on no customer job
  'cylinder.releaseQuarantine': Infer<typeof S.releaseCylinderQuarantine>;
  // Slice 4B — moving between our own yards, and settling a yard never recorded
  'cylinder.sendToBranch': Infer<typeof S.sendCylinderToBranch>;
  'cylinder.receiveAtBranch': Infer<typeof S.receiveCylinderAtBranch>;
  'cylinder.resolveYard': Infer<typeof S.resolveCylinderYard>;
  'job.resolveOwnershipDispute': Infer<typeof S.resolveOwnershipDispute>;
  'job.resolveContestedIntake': Infer<typeof S.resolveContestedIntake>;
  // 0100/DEF-159 — typed for the client even though no screen calls it yet: the
  // command exists and the owner may drive it through the door directly.
  'billing.activateRentPolicy': Infer<typeof S.activateRentPolicy>;
  'job.recordIntakeSafetyAction': Infer<typeof S.recordIntakeSafetyAction>;
  'job.recordLateIntakeSafetyAction': Infer<typeof S.recordLateIntakeSafetyAction>;
  'job.fill': Infer<typeof S.recordCylinderFill>;
  'job.prepare': Infer<typeof S.prepareCylinderForRefill>;
  'job.qc': Infer<typeof S.recordCylinderQc>;
  'job.return': Infer<typeof S.returnCustomerCylinders>;
  'job.bill': Infer<typeof S.billCustomerJob>;
  // 0145 — the company's own filling cycle
  'fill.record': Infer<typeof S.recordPlantFill>;
  'fill.check': Infer<typeof S.checkPlantFill>;
  'fill.prepare': Infer<typeof S.preparePlantCylinder>;
}
export type CommandName = keyof CommandBodies;

/** The commands that REQUIRE an Idempotency-Key (DEF-088): the client mints one per call and keeps it for the retry. */
export const RETRY_SENSITIVE: ReadonlySet<CommandName> = new Set<CommandName>([
  'challan.approve', 'dispatch.depart', 'dispatch.confirmDelivery', 'dispatch.resolveShortage', 'cylinder.bulkRegister', 'cylinder.registerDuplicate',
  'cutover.load', 'cutover.acceptOpeningBalance', 'cutover.importChallans', 'cutover.importReceipts', 'cutover.importTollFill', 'challan.reverse', 'payment.reverse', 'sd.refund', 'creditnote.issue', 'bill.send', 'payment.record', 'payment.allocate', 'payment.reverseAllocation', 'eventbill.resolveLate', 'notice.setState', 'costbill.issue',
  'price.activateList', 'price.assign', 'price.closeAssignment', 'site.setTerms', 'pricing.switch',
  'quote.issue', 'quote.revise', 'quote.decide', 'quote.approve',
  'order.amend', 'order.allocate',
  'po.approve', 'po.amend',
  // 0108 (DEF-168) — a retry must not put a second test on a cylinder's permanent
  // safety record, nor a second condemnation on one already condemned.
  'cylinder.recordMaintenance', 'cylinder.correctMaintenance', 'cylinder.condemn',
  'cylinder.finalizeDestruction',
  'cylinder.storeMaintenancePaper',
  'tender.submitBid', 'tender.addRequirement', 'tender.supersedeRequirement', 'tender.addDocument', 'tender.supersedeDocument',
  'damage.assess', 'damage.dispute', 'damage.uphold', 'damage.reduce', 'damage.waive', 'damage.void',
  'job.receive', 'job.inspect', 'job.releaseQuarantine', 'cylinder.releaseQuarantine', 'job.resolveOwnershipDispute',
  'cylinder.sendToBranch', 'cylinder.receiveAtBranch', 'cylinder.resolveYard',
  'job.resolveContestedIntake', 'job.recordIntakeSafetyAction', 'job.recordLateIntakeSafetyAction',
  'billing.activateRentPolicy',
  // 0152 — a retry must not write a second movement into a book that is never edited,
  // nor move the isolation nomination twice while somebody reads the first answer.
  'yard.openArea', 'yard.closeArea', 'yard.nominateQuarantineArea', 'cylinder.moveToArea',
  // 0160 — a retry must not write a second finding into an insert-only book.
  'cylinder.inspect',
  // 0081 — a fill writes events and the contents fact, a check may quarantine,
  // going home mints the outward note's number, and the charge moves money
  'job.fill', 'job.prepare', 'job.qc', 'job.return', 'job.bill',
  // 0145 — and the company's own cycle, for the same reasons: a fill writes events and the
  // contents fact, a check closes the work commitment, and a preparation empties a drum.
  'fill.record', 'fill.check', 'fill.prepare',
  // Slice 3 — a dropped connection on any of these costs PAPER: a second run puts a second
  // sheet of thirty identical stickers in front of somebody with nothing to say which to
  // throw away, a second sheet files a second document, and a second confirmation records a
  // print that did not happen.
  'label.prepare', 'label.reprint', 'label.generate', 'label.confirmPrinted',
]);

/** The query and the answer of each view. */
export interface Views {
  'home.worklist': { query: Infer<typeof home.worklistQuery>; result: Awaited<ReturnType<typeof home.worklist>> };
  'home.figures': { query: Infer<typeof figures.figuresQuery>; result: Awaited<ReturnType<typeof figures.figures>> };
  'home.health': { query: Infer<typeof health.healthQuery>; result: Awaited<ReturnType<typeof health.health>> };
  'approvals.book': { query: Infer<typeof approvalsBook.bookQuery>; result: Awaited<ReturnType<typeof approvalsBook.book>> };
  'customers.list': { query: Infer<typeof customers.listQuery>; result: Awaited<ReturnType<typeof customers.list>> };
  'customers.get': { query: Infer<typeof customers.getQuery>; result: Awaited<ReturnType<typeof customers.detail>> };
  'customers.summary': { query: Infer<typeof customers.summaryQuery>; result: Awaited<ReturnType<typeof customers.summary>> };
  'sites.list': { query: Infer<typeof customers.sitesQuery>; result: Awaited<ReturnType<typeof customers.sites>> };
  'states.list': { query: Infer<typeof customers.statesQuery>; result: Awaited<ReturnType<typeof customers.states>> };
  'customers.readiness': { query: Infer<typeof customers.readinessQuery>; result: Awaited<ReturnType<typeof customers.readiness>> };
  'documents.list': { query: Infer<typeof documents.listQuery>; result: Awaited<ReturnType<typeof documents.list>> };
  'customers.cylinderFills': { query: Infer<typeof customers.cylinderFillsQuery>; result: Awaited<ReturnType<typeof customers.cylinderFills>> };   // 10 Oct — the fill ledger
  'customerJobs.list': { query: Infer<typeof production.listQuery>; result: Awaited<ReturnType<typeof production.list>> };
  'customerJobs.cylinders': { query: Infer<typeof production.cylindersQuery>; result: Awaited<ReturnType<typeof production.cylinders>> };
  'contestedIntakes.worklist': { query: Infer<typeof contestedIntakes.worklistQuery>; result: Awaited<ReturnType<typeof contestedIntakes.worklist>> };
  'contestedIntakes.detail': { query: Infer<typeof contestedIntakes.detailQuery>; result: Awaited<ReturnType<typeof contestedIntakes.detail>> };
  'orders.list': { query: Infer<typeof orders.listQuery>; result: Awaited<ReturnType<typeof orders.list>> };
  'orders.get': { query: Infer<typeof orders.getQuery>; result: Awaited<ReturnType<typeof orders.detail>> };
  'cylinders.search': { query: Infer<typeof cylinders.searchQuery>; result: Awaited<ReturnType<typeof cylinders.search>> };
  'cylinders.resolve': { query: Infer<typeof cylinders.resolveQuery>; result: Awaited<ReturnType<typeof cylinders.resolve>> };
  'labels.stocks': { query: Infer<typeof labelViews.stocksQuery>; result: Awaited<ReturnType<typeof labelViews.stocks>> };
  'labels.waiting': { query: Infer<typeof labelViews.waitingQuery>; result: Awaited<ReturnType<typeof labelViews.waiting>> };
  'labels.batches': { query: Infer<typeof labelViews.batchesQuery>; result: Awaited<ReturnType<typeof labelViews.batches>> };
  'labels.batch': { query: Infer<typeof labelViews.batchQuery>; result: Awaited<ReturnType<typeof labelViews.batch>> };
  'labels.scans': { query: Infer<typeof labelViews.scansQuery>; result: Awaited<ReturnType<typeof labelViews.scans>> };
  'labels.historic': { query: Infer<typeof labelViews.historicQuery>; result: Awaited<ReturnType<typeof labelViews.historic>> };
  'cylinders.passport': { query: Infer<typeof passportView.passportQuery>; result: Awaited<ReturnType<typeof passportView.passport>> };
  // Slice 4 — the damage lifecycle and the quarantine queue
  'damage.list': { query: Infer<typeof damageViews.listQuery>; result: Awaited<ReturnType<typeof damageViews.list>> };
  'damage.cylinders': { query: Infer<typeof damageViews.cylindersQuery>; result: Awaited<ReturnType<typeof damageViews.cylinders>> };
  'damage.get': { query: Infer<typeof damageViews.getQuery>; result: Awaited<ReturnType<typeof damageViews.detail>> };
  'quarantine.list': { query: Infer<typeof damageViews.quarantineQuery>; result: Awaited<ReturnType<typeof damageViews.quarantine>> };
  'cylinders.yards': { query: Infer<typeof damageViews.yardQuery>; result: Awaited<ReturnType<typeof damageViews.yards>> };
  'maintenance.book': { query: Infer<typeof maintenance.bookQuery>; result: Awaited<ReturnType<typeof maintenance.book>> };
  'maintenance.due': { query: Infer<typeof maintenance.worklistQuery>; result: Awaited<ReturnType<typeof maintenance.worklist>> };
  'cylinders.pendingDestruction': { query: Infer<typeof maintenance.pendingDestructionQuery>; result: Awaited<ReturnType<typeof maintenance.pendingDestruction>> };
  'cylinders.overdue': { query: Infer<typeof cylinders.overdueQuery>; result: Awaited<ReturnType<typeof cylinders.overdue>> };
  'cylinders.census': { query: Infer<typeof cylinders.censusQuery>; result: Awaited<ReturnType<typeof cylinders.census>> };
  'challans.list': { query: Infer<typeof dispatch.listQuery>; result: Awaited<ReturnType<typeof dispatch.list>> };
  'challans.get': { query: Infer<typeof dispatch.getQuery>; result: Awaited<ReturnType<typeof dispatch.detail>> };
  'account.holdings': { query: Infer<typeof account.holdingsQuery>; result: Awaited<ReturnType<typeof account.holdings>> };
  'account.balance': { query: Infer<typeof account.balanceQuery>; result: Awaited<ReturnType<typeof account.balance>> };
  'account.aging': { query: Infer<typeof account.agingQuery>; result: Awaited<ReturnType<typeof account.aging>> };
  'payments.list': { query: Infer<typeof payments.paymentsQuery>; result: Awaited<ReturnType<typeof payments.payments>> };
  'account.reconciliation': { query: Infer<typeof allocationViews.reconciliationQuery>; result: Awaited<ReturnType<typeof allocationViews.reconciliation>> };
  'account.unallocated': { query: Infer<typeof allocationViews.unallocatedQuery>; result: Awaited<ReturnType<typeof allocationViews.unallocated>> };
  'account.statement': { query: Infer<typeof statementView.statementQuery>; result: Awaited<ReturnType<typeof statementView.statement>> };
  'audit.entity': { query: Infer<typeof audit.entityQuery>; result: Awaited<ReturnType<typeof audit.forEntity>> };
  'queue.health': { query: Infer<typeof outbox.healthQuery>; result: Awaited<ReturnType<typeof outbox.health>> };
  'outbox.list': { query: Infer<typeof outbox.listQuery>; result: Awaited<ReturnType<typeof outbox.list>> };
  'cutover.runs': { query: Infer<typeof cutoverViews.runsQuery>; result: Awaited<ReturnType<typeof cutoverViews.runs>> };
  'cutover.findings': { query: Infer<typeof cutoverViews.findingsQuery>; result: Awaited<ReturnType<typeof cutoverViews.findings>> };
  'cutover.worklist': { query: Infer<typeof cutoverViews.worklistQuery>; result: Awaited<ReturnType<typeof cutoverViews.worklist>> };
  'bills.get': { query: Infer<typeof bills.getQuery>; result: Awaited<ReturnType<typeof bills.detail>> };
  'bills.list': { query: Infer<typeof bills.listQuery>; result: Awaited<ReturnType<typeof bills.list>> };
  'bills.revenue': { query: Infer<typeof bills.revenueQuery>; result: Awaited<ReturnType<typeof bills.revenue>> };
  // the owner's three questions (9 Oct): what was sold, to whom, what moved
  'reports.gasSold': { query: Infer<typeof reports.gasSoldQuery>; result: Awaited<ReturnType<typeof reports.gasSold>> };
  'reports.bestCustomers': { query: Infer<typeof reports.bestCustomersQuery>; result: Awaited<ReturnType<typeof reports.bestCustomers>> };
  'reports.dayBook': { query: Infer<typeof reports.dayBookQuery>; result: Awaited<ReturnType<typeof reports.dayBook>> };
  'reports.fills': { query: Infer<typeof reports.fillsQuery>; result: Awaited<ReturnType<typeof reports.fills>> };
  'sites.terms': { query: Infer<typeof customers.siteTermsQuery>; result: Awaited<ReturnType<typeof customers.siteTerms>> };
  'jobs.list': { query: Infer<typeof outbox.jobsQuery>; result: Awaited<ReturnType<typeof outbox.jobs>> };
  'findings.list': { query: Infer<typeof outbox.findingsQuery>; result: Awaited<ReturnType<typeof outbox.findings>> };
  // 0152 — the yard's own geography, its movement book and its correction queue
  'yard.branches': { query: Infer<typeof yard.branchesQuery>; result: Awaited<ReturnType<typeof yard.branches>> };
  'yard.areas': { query: Infer<typeof yard.areasQuery>; result: Awaited<ReturnType<typeof yard.areas>> };
  'yard.standing': { query: Infer<typeof yard.standingQuery>; result: Awaited<ReturnType<typeof yard.standing>> };
  'yard.movements': { query: Infer<typeof yard.movementsQuery>; result: Awaited<ReturnType<typeof yard.movements>> };
  'yard.unrecorded': { query: Infer<typeof yard.unrecordedQuery>; result: Awaited<ReturnType<typeof yard.unrecorded>> };
  'yard.stages': { query: Infer<typeof yard.stagesQuery>; result: Awaited<ReturnType<typeof yard.stages>> };
  'filling.floor': { query: Infer<typeof filling.floorQuery>; result: Awaited<ReturnType<typeof filling.floor>> };
  'filling.batches': { query: Infer<typeof filling.batchesQuery>; result: Awaited<ReturnType<typeof filling.batches>> };
  'filling.cycles': { query: Infer<typeof filling.cyclesQuery>; result: Awaited<ReturnType<typeof filling.cycles>> };
  'filling.stats': { query: Infer<typeof filling.statsQuery>; result: Awaited<ReturnType<typeof filling.stats>> };
  'filling.waiting': { query: Infer<typeof filling.waitingQuery>; result: Awaited<ReturnType<typeof filling.waiting>> };
  'filling.filled': { query: Infer<typeof filling.filledQuery>; result: Awaited<ReturnType<typeof filling.filled>> };
  'filling.history': { query: Infer<typeof filling.historyQuery>; result: Awaited<ReturnType<typeof filling.history>> };
  'filling.byGas': { query: Infer<typeof filling.byGasQuery>; result: Awaited<ReturnType<typeof filling.byGas>> };
  'filling.byCustomer': { query: Infer<typeof filling.byCustomerQuery>; result: Awaited<ReturnType<typeof filling.byCustomer>> };
  // Milestone 2 — commercial master data
  'branches.list': { query: Infer<typeof masters.branchesQuery>; result: Awaited<ReturnType<typeof masters.branches>> };
  'catalogue.get': { query: Infer<typeof masters.catalogueQuery>; result: Awaited<ReturnType<typeof masters.catalogue>> };
  'skus.list': { query: Infer<typeof masters.skusQuery>; result: Awaited<ReturnType<typeof masters.skus>> };
  'gases.list': { query: Infer<typeof masters.gasServicesQuery>; result: Awaited<ReturnType<typeof masters.gasServices>> };
  'charges.list': { query: Infer<typeof masters.chargeSchedulesQuery>; result: Awaited<ReturnType<typeof masters.chargeSchedules>> };
  'offertext.list': { query: Infer<typeof masters.offerTextQuery>; result: Awaited<ReturnType<typeof masters.offerText>> };
  'company.letterhead': { query: Infer<typeof masters.letterheadQuery>; result: Awaited<ReturnType<typeof masters.letterhead>> };
  'rates.agreed': { query: Infer<typeof masters.agreedRatesQuery>; result: Awaited<ReturnType<typeof masters.agreedRates>> };
  'setup.state': { query: Record<string, never>; result: Awaited<ReturnType<typeof setup.setupState>> };
  'taxcodes.list': { query: Infer<typeof masters.taxCodesQuery>; result: Awaited<ReturnType<typeof masters.taxCodes>> };
  'pricelists.list': { query: Infer<typeof masters.priceListsQuery>; result: Awaited<ReturnType<typeof masters.priceLists>> };
  'pricelists.get': { query: Infer<typeof masters.priceListQuery>; result: Awaited<ReturnType<typeof masters.priceList>> };
  'calendar.get': { query: Infer<typeof masters.calendarQuery>; result: Awaited<ReturnType<typeof masters.calendar>> };
  'calendar.rentPolicy': { query: Infer<typeof masters.rentPolicyQuery>; result: Awaited<ReturnType<typeof masters.rentPolicy>> };
  'priceassign.list': { query: Infer<typeof masters.priceAssignmentsQuery>; result: Awaited<ReturnType<typeof masters.priceAssignments>> };
  'pricing.reconcile': { query: Infer<typeof masters.reconciliationQuery>; result: Awaited<ReturnType<typeof masters.reconciliation>> };
  'pricing.mode': { query: Infer<typeof masters.pricingModeQuery>; result: Awaited<ReturnType<typeof masters.pricingMode>> };
  'users.list': { query: Infer<typeof people.usersQuery>; result: Awaited<ReturnType<typeof people.users>> };
  'users.get': { query: Infer<typeof people.userQuery>; result: Awaited<ReturnType<typeof people.user>> };
  'roles.list': { query: Infer<typeof people.rolesQuery>; result: Awaited<ReturnType<typeof people.roles>> };
  'roles.get': { query: Infer<typeof people.roleQuery>; result: Awaited<ReturnType<typeof people.role>> };
  'functions.list': { query: Infer<typeof people.functionsQuery>; result: Awaited<ReturnType<typeof people.functions>> };
  'customers.party': { query: Infer<typeof people.partyQuery>; result: Awaited<ReturnType<typeof people.party>> };
  'jobperiods.list': { query: Infer<typeof outbox.jobPeriodsQuery>; result: Awaited<ReturnType<typeof outbox.jobPeriods>> };
  'jobperiods.attempts': { query: Infer<typeof outbox.jobPeriodAttemptsQuery>; result: Awaited<ReturnType<typeof outbox.jobPeriodAttempts>> };
  'quotes.list': { query: Infer<typeof quotes.listQuery>; result: Awaited<ReturnType<typeof quotes.list>> };
  'quotes.get': { query: Infer<typeof quotes.getQuery>; result: Awaited<ReturnType<typeof quotes.detail>> };
  'quotes.stats': { query: Infer<typeof quotes.statsQuery>; result: Awaited<ReturnType<typeof quotes.stats>> };
  'orders.fulfilment': { query: Infer<typeof fulfilment.getQuery>; result: Awaited<ReturnType<typeof fulfilment.detail>> };
  'suppliers.list': { query: Infer<typeof purchasing.suppliersQuery>; result: Awaited<ReturnType<typeof purchasing.suppliers>> };
  'purchases.list': { query: Infer<typeof purchasing.listQuery>; result: Awaited<ReturnType<typeof purchasing.list>> };
  'purchases.get': { query: Infer<typeof purchasing.getQuery>; result: Awaited<ReturnType<typeof purchasing.detail>> };
  'tenders.list': { query: Infer<typeof tenders.listQuery>; result: Awaited<ReturnType<typeof tenders.list>> };
  'tenders.get': { query: Infer<typeof tenders.getQuery>; result: Awaited<ReturnType<typeof tenders.detail>> };
  'tenders.requirements': { query: Infer<typeof tenders.requirementsQuery>; result: Awaited<ReturnType<typeof tenders.requirements>> };
  'tenders.documents': { query: Infer<typeof tenders.documentsQuery>; result: Awaited<ReturnType<typeof tenders.documents>> };
}
export type ViewName = keyof Views;

export interface Issue { path: string; message: string }

/** Every refusal, as the door said it: the code decides what a screen does, the message is what it shows, the issues go next to their fields. */
export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly issues: readonly Issue[] = [], public readonly reference?: string) {
    super(message);
    this.name = 'ApiError';
  }
  /** The session is gone: the screen goes back to sign-in. */
  get signedOut(): boolean { return this.status === 401; }
  /** The user lacks the function: the screen says so and hides nothing else. */
  get forbidden(): boolean { return this.status === 403; }
  /** A field-level problem the user can fix. */
  get fixable(): boolean { return this.status === 422 || this.status === 400; }
}

export interface Transport {
  send(req: { method: string; path: string; headers: Record<string, string>; body?: string }): Promise<{ status: number; headers: Record<string, string>; body: string }>;
}

export interface ClientOptions {
  transport: Transport;
  /** Mints an idempotency key: 8–128 characters of [A-Za-z0-9._-]. The browser passes crypto.randomUUID. */
  newKey: () => string;
}

export class ApiClient {
  constructor(private readonly o: ClientOptions) {}

  async login(username: string, password: string): Promise<void> { await this.call('POST', '/login', { username, password }); }
  async logout(): Promise<void> { await this.call('POST', '/logout'); }
  async changePassword(currentPassword: string, newPassword: string): Promise<void> { await this.call('POST', '/password', { currentPassword, newPassword }); }
  /** Who the session is — the company is the server's answer, never something a screen sends. */
  whoami(): Promise<{
    actorId: number; roles: string[];
    functions: string[];
    /** 0121 — the yard this session is working at, or null to use the person's default. */
    activeBranchId: number | null;
    /** The OPEN branches this person holds, for the picker. One or none means no picker. */
    branches: { branchId: number; code: string; name: string; isDefault: boolean }[];
  }> { return this.call('GET', '/whoami'); }

  /**
   * 0121 — say which yard this session is working at. `null` withdraws the choice and falls
   * back to the person's standing default.
   *
   * Not a command: it changes nothing in the register, and the authority was settled when
   * somebody granted the branch. The door refuses a branch the person does not hold and one
   * that is closed.
   */
  setSessionBranch(branchId: number | null): Promise<{ ok: boolean; value: { activeBranchId: number | null } }> {
    return this.call('POST', '/session/branch', { branchId });
  }

  /**
   * Run a command. A retry-sensitive command gets a key minted here unless
   * the caller passes the one it kept from the attempt that timed out — a
   * screen that retries MUST pass the same key, or the server sees a second
   * request (DEF-088).
   */
  async command<N extends CommandName>(name: N, body: CommandBodies[N], opts: { idempotencyKey?: string } = {}): Promise<{ value: unknown; idempotencyKey?: string }> {
    const headers: Record<string, string> = {};
    let key: string | undefined;
    if (RETRY_SENSITIVE.has(name)) { key = opts.idempotencyKey ?? this.o.newKey(); headers[IDEMPOTENCY_HEADER] = key; }
    try {
      const r = await this.call<{ value: unknown }>('POST', `/commands/${name}`, body, headers);
      return key ? { value: r.value, idempotencyKey: key } : { value: r.value };
    } catch (e) {
      /**
       * R20261005-01 — THE KEY SURVIVES AN ANSWER THAT NEVER CAME.
       *
       * `useRun` has always been written to keep a retry key across a dropped connection and
       * send the same one again. Nothing ever gave it one: the key was minted here, used for
       * the header, and lost with the stack when the transport rejected — so the retry minted
       * a fresh key and the door, correctly, treated it as a SECOND REQUEST. For
       * `payment.record` that is a second payment and a second receipt, from one operator
       * pressing the button again after the wheel stopped spinning.
       *
       * Attached only when the transport gave NO ANSWER AT ALL. An `ApiError` means the door
       * answered: the command either refused or threw, its transaction rolled back, and that
       * attempt is over — keeping the key there would make a later, different request replay
       * an answer it never earned. The uncertain case is the silent one, and it is the only
       * one that gets the key back.
       */
      if (key !== undefined && !(e instanceof ApiError) && e instanceof Error) {
        (e as Error & { retryKey?: string }).retryKey = key;
      }
      throw e;
    }
  }

  async view<N extends ViewName>(name: N, query: Views[N]['query']): Promise<Views[N]['result']> {
    const qs = Object.entries(query as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&');
    const r = await this.call<{ value: Views[N]['result'] }>('GET', `/views/${name}${qs ? `?${qs}` : ''}`);
    return r.value;
  }

  /** Where a document's PDF is; the browser opens it in a new tab with the session cookie. */
  documentUrl(documentId: number): string { return `/documents/${documentId}.pdf`; }
  /** Where a filed tender paper's bytes are (0065); the door answers with attachment-only headers, so the browser saves it. */
  attachmentUrl(attachmentId: number): string { return `/documents/attachments/${attachmentId}`; }

  /** The challan register report for a customer — drawn fresh on request, never filed (a report, not a paper). */
  challanRegisterUrl(customerId: number, q: { from?: string; to?: string; type?: 'dispatch' | 'return' } = {}): string {
    const params = [`customerId=${customerId}`,
      ...(q.from ? [`from=${q.from}`] : []), ...(q.to ? [`to=${q.to}`] : []), ...(q.type ? [`type=${q.type}`] : [])];
    return `/reports/challan-register.pdf?${params.join('&')}`;
  }

  /** The customer statement as A4 — drawn fresh on request, never filed. */
  statementUrl(customerId: number, q: { from?: string; to?: string } = {}): string {
    const params = [`customerId=${customerId}`, ...(q.from ? [`from=${q.from}`] : []), ...(q.to ? [`to=${q.to}`] : [])];
    return `/reports/statement.pdf?${params.join('&')}`;
  }

  private async call<T>(method: string, path: string, body?: unknown, extra: Record<string, string> = {}): Promise<T> {
    const r = await this.o.transport.send({ method, path, headers: { ...(body === undefined ? {} : { 'content-type': 'application/json' }), ...extra },
                                            ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    let parsed: Record<string, unknown> = {};
    try { parsed = JSON.parse(r.body) as Record<string, unknown>; } catch { /* a non-JSON answer is reported below with its status */ }
    if (r.status >= 200 && r.status < 300) return parsed as T;
    throw new ApiError(r.status, String(parsed['error'] ?? 'unknown'), String(parsed['message'] ?? `the server answered ${r.status}`),
      (parsed['issues'] as Issue[] | undefined) ?? [], typeof parsed['reference'] === 'string' ? parsed['reference'] : undefined);
  }
}
